import { useState, useEffect } from '@wordpress/element';
import { __, sprintf } from '@wordpress/i18n';
import HeaderFooterConditions from './HeaderFooterConditions';
import './HeaderFooterBuilder.scss';

const HeaderFooterBuilder = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showConditionsModal, setShowConditionsModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const [createData, setCreateData] = useState({
    title: '',
    type: 'header',
    priority: 10,
    conditions: null,
  });

  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${window.thhaAdminSettings.ajaxurl}?action=thha_hf_get_templates&nonce=${window.thhaAdminSettings.nonce}`
      );
      const data = await response.json();
      if (data.success) {
        setTemplates(data.data || []);
      }
    } catch (e) {
      console.error('Error fetching templates:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateModal = (type = 'header') => {
    setCreateData({
      title: '',
      type,
      priority: 10,
      conditions: {
        version: 2,
        relation: 'any',
        exclude_relation: 'any',
        conditions: [
          {
            id: Date.now(),
            node_type: 'condition',
            type: 'everywhere',
            logic: 'show',
            specific_items: [],
            categories: [],
            taxonomies: [],
            post_type_value: 'post',
            taxonomy_value: 'category',
          },
        ],
      },
    });
    setShowCreateModal(true);
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const formData = new URLSearchParams();
      formData.append('action', 'thha_hf_create_template');
      formData.append('nonce', window.thhaAdminSettings.saveNonce || window.thhaAdminSettings.nonce);
      formData.append('title', createData.title);
      formData.append('type', createData.type);
      formData.append('priority', createData.priority);
      formData.append('is_active', '1');
      if (createData.conditions) {
        formData.append('conditions', JSON.stringify(createData.conditions));
      }

      const response = await fetch(window.thhaAdminSettings.ajaxurl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });

      const res = await response.json();
      if (res.success && res.data?.elementor_edit_url) {
        if (window.iziToast) {
          window.iziToast.success({
            title: __('Success', 'huge-addons-for-elementor'),
            message: __('Template created! Opening Elementor editor...', 'huge-addons-for-elementor'),
            position: 'topRight',
          });
        }
        window.location.href = res.data.elementor_edit_url;
      } else {
        if (window.iziToast) {
          window.iziToast.error({
            title: __('Error', 'huge-addons-for-elementor'),
            message: res.data?.message || __('Failed to create template.', 'huge-addons-for-elementor'),
            position: 'topRight',
          });
        }
      }
    } catch (err) {
      console.error('Error creating template:', err);
    } finally {
      setCreating(false);
    }
  };

  const handleToggleStatus = async (template) => {
    const newStatus = template.status === 'publish' ? 'draft' : 'publish';
    try {
      const formData = new URLSearchParams();
      formData.append('action', 'thha_hf_update_template');
      formData.append('nonce', window.thhaAdminSettings.saveNonce || window.thhaAdminSettings.nonce);
      formData.append('template_id', template.id);
      formData.append('status', newStatus);

      const response = await fetch(window.thhaAdminSettings.ajaxurl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });

      const res = await response.json();
      if (res.success) {
        setTemplates((prev) =>
          prev.map((t) => (t.id === template.id ? { ...t, status: newStatus } : t))
        );
        if (window.iziToast) {
          window.iziToast.success({
            title: __('Updated', 'huge-addons-for-elementor'),
            message: sprintf(__('Status changed to %s.', 'huge-addons-for-elementor'), newStatus),
            position: 'topRight',
          });
        }
      } else {
        if (window.iziToast) {
          window.iziToast.warning({
            title: __('Notice', 'huge-addons-for-elementor'),
            message: res.data?.message || __('Could not update status.', 'huge-addons-for-elementor'),
            position: 'topRight',
          });
        }
      }
    } catch (e) {
      console.error('Status toggle error:', e);
    }
  };

  const handleDuplicate = async (templateId) => {
    try {
      const formData = new URLSearchParams();
      formData.append('action', 'thha_hf_duplicate_template');
      formData.append('nonce', window.thhaAdminSettings.saveNonce || window.thhaAdminSettings.nonce);
      formData.append('template_id', templateId);

      const response = await fetch(window.thhaAdminSettings.ajaxurl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });

      const res = await response.json();
      if (res.success && res.data?.template) {
        setTemplates((prev) => [res.data.template, ...prev]);
        if (window.iziToast) {
          window.iziToast.success({
            title: __('Duplicated', 'huge-addons-for-elementor'),
            message: __('Template duplicated as draft.', 'huge-addons-for-elementor'),
            position: 'topRight',
          });
        }
      }
    } catch (e) {
      console.error('Duplicate error:', e);
    }
  };

  const handleDelete = async (templateId, templateTitle) => {
    if (!window.confirm(sprintf(__('Are you sure you want to delete "%s"?', 'huge-addons-for-elementor'), templateTitle))) {
      return;
    }

    try {
      const formData = new URLSearchParams();
      formData.append('action', 'thha_hf_delete_template');
      formData.append('nonce', window.thhaAdminSettings.saveNonce || window.thhaAdminSettings.nonce);
      formData.append('template_id', templateId);

      const response = await fetch(window.thhaAdminSettings.ajaxurl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });

      const res = await response.json();
      if (res.success) {
        setTemplates((prev) => prev.filter((t) => t.id !== templateId));
        if (window.iziToast) {
          window.iziToast.success({
            title: __('Deleted', 'huge-addons-for-elementor'),
            message: __('Template deleted.', 'huge-addons-for-elementor'),
            position: 'topRight',
          });
        }
      }
    } catch (e) {
      console.error('Delete error:', e);
    }
  };

  const renderConditionsBadge = (template) => {
    const count = template.conditions_count || 0;
    const condList = template.conditions?.conditions || [];
    const firstType = condList[0]?.type || 'everywhere';

    let label = __('Everywhere', 'huge-addons-for-elementor');
    if (firstType === 'front_page') label = __('Front Page', 'huge-addons-for-elementor');
    if (firstType === 'singular') label = __('Singular', 'huge-addons-for-elementor');
    if (firstType === 'archive') label = __('Archives', 'huge-addons-for-elementor');

    if (count > 1) {
      label += ` (+${count - 1})`;
    }

    return (
      <button
        type="button"
        className="thha-badge-conditions"
        onClick={() => {
          setSelectedTemplate(template);
          setShowConditionsModal(true);
        }}
        title={__('Click to edit display conditions', 'huge-addons-for-elementor')}
      >
        <span className="thha-badge-icon">⚙</span> {label}
      </button>
    );
  };

  const filteredTemplates = templates.filter((t) => {
    if (filterType === 'all') return true;
    return t.type === filterType;
  });

  return (
    <div className="thha-hf-builder">
      {/* Top Banner & Header */}
      <div className="thha-hf-header-bar">
        <div className="thha-hf-title-wrap">
          <h2 className="thha-hf-title">{__('Header & Footer Builder', 'huge-addons-for-elementor')}</h2>
          <p className="thha-hf-subtitle">
            {__(
              'Create unlimited custom headers and footers using Elementor with advanced display condition logic.',
              'huge-addons-for-elementor'
            )}
          </p>
        </div>
        <div className="thha-hf-actions">
          <button
            type="button"
            className="thha-btn-primary"
            onClick={() => handleOpenCreateModal('header')}
          >
            + {__('Add New Header', 'huge-addons-for-elementor')}
          </button>
          <button
            type="button"
            className="thha-btn-secondary"
            onClick={() => handleOpenCreateModal('footer')}
          >
            + {__('Add New Footer', 'huge-addons-for-elementor')}
          </button>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="thha-hf-filters-bar">
        <div className="thha-hf-filter-pills">
          <button
            type="button"
            className={`thha-filter-pill ${filterType === 'all' ? 'is-active' : ''}`}
            onClick={() => setFilterType('all')}
          >
            {__('All Templates', 'huge-addons-for-elementor')} ({templates.length})
          </button>
          <button
            type="button"
            className={`thha-filter-pill ${filterType === 'header' ? 'is-active' : ''}`}
            onClick={() => setFilterType('header')}
          >
            {__('Headers', 'huge-addons-for-elementor')} (
            {templates.filter((t) => t.type === 'header').length})
          </button>
          <button
            type="button"
            className={`thha-filter-pill ${filterType === 'footer' ? 'is-active' : ''}`}
            onClick={() => setFilterType('footer')}
          >
            {__('Footers', 'huge-addons-for-elementor')} (
            {templates.filter((t) => t.type === 'footer').length})
          </button>
        </div>
      </div>

      {/* Templates Table or Empty State */}
      {loading ? (
        <div className="thha-hf-loading">
          <span className="thha-spinner"></span>
          <p>{__('Loading templates...', 'huge-addons-for-elementor')}</p>
        </div>
      ) : filteredTemplates.length === 0 ? (
        <div className="thha-hf-empty-state">
          <div className="thha-empty-icon">🎨</div>
          <h3>{__('No templates found', 'huge-addons-for-elementor')}</h3>
          <p>
            {__(
              'Get started by designing your first header or footer with the Elementor visual drag-and-drop editor.',
              'huge-addons-for-elementor'
            )}
          </p>
          <div className="thha-empty-actions">
            <button
              type="button"
              className="thha-btn-primary"
              onClick={() => handleOpenCreateModal('header')}
            >
              + {__('Create Header', 'huge-addons-for-elementor')}
            </button>
            <button
              type="button"
              className="thha-btn-secondary"
              onClick={() => handleOpenCreateModal('footer')}
            >
              + {__('Create Footer', 'huge-addons-for-elementor')}
            </button>
          </div>
        </div>
      ) : (
        <div className="thha-hf-table-card">
          <table className="thha-hf-table">
            <thead>
              <tr>
                <th>{__('Title', 'huge-addons-for-elementor')}</th>
                <th>{__('Type', 'huge-addons-for-elementor')}</th>
                <th>{__('Conditions', 'huge-addons-for-elementor')}</th>
                <th>{__('Priority', 'huge-addons-for-elementor')}</th>
                <th>{__('Status', 'huge-addons-for-elementor')}</th>
                <th>{__('Author', 'huge-addons-for-elementor')}</th>
                <th>{__('Date', 'huge-addons-for-elementor')}</th>
                <th className="thha-text-right">{__('Actions', 'huge-addons-for-elementor')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredTemplates.map((template) => (
                <tr key={template.id}>
                  <td className="thha-cell-title">
                    <a
                      href={template.elementor_edit_url}
                      className="thha-template-title-link"
                      title={__('Edit with Elementor', 'huge-addons-for-elementor')}
                    >
                      {template.title}
                    </a>
                  </td>
                  <td>
                    <span className={`thha-type-pill is-${template.type}`}>
                      {template.type === 'footer'
                        ? __('Footer', 'huge-addons-for-elementor')
                        : __('Header', 'huge-addons-for-elementor')}
                    </span>
                  </td>
                  <td>{renderConditionsBadge(template)}</td>
                  <td>
                    <span className="thha-priority-num">{template.priority || 10}</span>
                  </td>
                  <td>
                    <label className="thha-switch-label">
                      <input
                        type="checkbox"
                        checked={template.status === 'publish'}
                        onChange={() => handleToggleStatus(template)}
                        className="thha-switch-input"
                      />
                      <span className="thha-switch-slider"></span>
                      <span className="thha-switch-tag">
                        {template.status === 'publish'
                          ? __('Active', 'huge-addons-for-elementor')
                          : __('Draft', 'huge-addons-for-elementor')}
                      </span>
                    </label>
                  </td>
                  <td className="thha-cell-muted">{template.author}</td>
                  <td className="thha-cell-muted">{template.date}</td>
                  <td className="thha-cell-actions thha-text-right">
                    <a
                      href={template.elementor_edit_url}
                      className="thha-btn-action is-edit"
                      title={__('Edit with Elementor', 'huge-addons-for-elementor')}
                    >
                      {__('Edit', 'huge-addons-for-elementor')}
                    </a>
                    <button
                      type="button"
                      className="thha-btn-action"
                      onClick={() => {
                        setSelectedTemplate(template);
                        setShowConditionsModal(true);
                      }}
                      title={__('Display Conditions', 'huge-addons-for-elementor')}
                    >
                      {__('Conditions', 'huge-addons-for-elementor')}
                    </button>
                    <button
                      type="button"
                      className="thha-btn-action"
                      onClick={() => handleDuplicate(template.id)}
                      title={__('Duplicate', 'huge-addons-for-elementor')}
                    >
                      {__('Clone', 'huge-addons-for-elementor')}
                    </button>
                    <button
                      type="button"
                      className="thha-btn-action is-danger"
                      onClick={() => handleDelete(template.id, template.title)}
                      title={__('Delete', 'huge-addons-for-elementor')}
                    >
                      {__('Delete', 'huge-addons-for-elementor')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Template Modal */}
      {showCreateModal && (
        <div className="thha-modal-backdrop" onClick={() => setShowCreateModal(false)}>
          <div className="thha-modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="thha-modal-header">
              <h3>
                {createData.type === 'footer'
                  ? __('Create New Footer', 'huge-addons-for-elementor')
                  : __('Create New Header', 'huge-addons-for-elementor')}
              </h3>
              <button
                type="button"
                className="thha-modal-close"
                onClick={() => setShowCreateModal(false)}
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleCreateSubmit}>
              <div className="thha-modal-body">
                <div className="thha-form-group">
                  <label htmlFor="template-title">
                    {__('Template Title', 'huge-addons-for-elementor')} *
                  </label>
                  <input
                    id="template-title"
                    type="text"
                    required
                    placeholder={
                      createData.type === 'footer' ? 'e.g. Main Footer' : 'e.g. Main Header'
                    }
                    value={createData.title}
                    onChange={(e) => setCreateData({ ...createData, title: e.target.value })}
                    className="thha-input"
                  />
                </div>

                <div className="thha-form-group">
                  <label htmlFor="template-type">
                    {__('Template Type', 'huge-addons-for-elementor')}
                  </label>
                  <select
                    id="template-type"
                    value={createData.type}
                    onChange={(e) => setCreateData({ ...createData, type: e.target.value })}
                    className="thha-select"
                  >
                    <option value="header">{__('Header', 'huge-addons-for-elementor')}</option>
                    <option value="footer">{__('Footer', 'huge-addons-for-elementor')}</option>
                  </select>
                </div>

                <div className="thha-form-group">
                  <label>{__('Display Conditions (Optional initial setup)', 'huge-addons-for-elementor')}</label>
                  <HeaderFooterConditions
                    isEmbedded={true}
                    initialConditions={createData.conditions}
                    initialPriority={createData.priority}
                    onChange={(payload, prio) => {
                      setCreateData((prev) => ({ ...prev, conditions: payload, priority: prio }));
                    }}
                  />
                </div>
              </div>
              <div className="thha-modal-footer">
                <button
                  type="button"
                  className="thha-btn-secondary"
                  onClick={() => setShowCreateModal(false)}
                >
                  {__('Cancel', 'huge-addons-for-elementor')}
                </button>
                <button
                  type="submit"
                  className="thha-btn-primary"
                  disabled={creating || !createData.title.trim()}
                >
                  {creating
                    ? __('Creating...', 'huge-addons-for-elementor')
                    : __('Create & Edit with Elementor', 'huge-addons-for-elementor')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Conditions Modal */}
      {showConditionsModal && selectedTemplate && (
        <div className="thha-modal-backdrop" onClick={() => setShowConditionsModal(false)}>
          <div className="thha-modal-dialog thha-modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="thha-modal-header">
              <div>
                <h3>{sprintf(__('Conditions for "%s"', 'huge-addons-for-elementor'), selectedTemplate.title)}</h3>
                <p className="thha-modal-subtitle">
                  {__(
                    'Configure where this template appears across your site. Higher specificity and lower priority numbers take precedence.',
                    'huge-addons-for-elementor'
                  )}
                </p>
              </div>
              <button
                type="button"
                className="thha-modal-close"
                onClick={() => setShowConditionsModal(false)}
              >
                ✕
              </button>
            </div>
            <div className="thha-modal-body">
              <HeaderFooterConditions
                templateId={selectedTemplate.id}
                initialConditions={selectedTemplate.conditions}
                initialStatus={selectedTemplate.status}
                initialPriority={selectedTemplate.priority}
                onSave={(newConditions, newPriority) => {
                  setTemplates((prev) =>
                    prev.map((t) =>
                      t.id === selectedTemplate.id
                        ? {
                            ...t,
                            conditions: newConditions,
                            conditions_count: newConditions?.conditions?.length || 0,
                            priority: newPriority,
                          }
                        : t
                    )
                  );
                  setShowConditionsModal(false);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HeaderFooterBuilder;
